/* Reference-driven Flash collection enhancement. No API, storage, tracking or cart mutations. */
(() => {
  if (customElements.get('skrt-flash-catalog')) return;

  class SkrtFlashCatalog extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.grid = this.querySelector('[data-flash-grid]');
      this.controls = this.querySelector('[data-flash-controls]');
      this.type = this.querySelector('[data-flash-type]');
      this.sort = this.querySelector('[data-flash-sort]');
      this.count = this.querySelector('[data-flash-count]');
      this.empty = this.querySelector('[data-flash-empty]');
      this.pagination = this.querySelector('[data-flash-pagination]');
      this.previous = this.querySelector('[data-flash-prev]');
      this.next = this.querySelector('[data-flash-next]');
      this.pageLabel = this.querySelector('[data-flash-page]');
      this.reset = this.querySelector('[data-flash-reset]');
      if (![this.grid, this.controls, this.type, this.sort, this.count, this.empty,
        this.pagination, this.previous, this.next, this.pageLabel, this.reset].every(Boolean)) return;

      this.cards = Array.from(this.grid.querySelectorAll('[data-flash-card]'));
      this.originalOrder = new Map(this.cards.map((card, index) => [card, index]));
      this.pageSize = 24;
      this.page = 1;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const types = [...new Set(this.cards.map(card => card.dataset.productType).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      while (this.type.options.length > 1) this.type.remove(1);
      for (const value of types) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        this.type.append(option);
      }
      this.controls.addEventListener('submit', event => event.preventDefault(), options);
      this.type.addEventListener('change', () => { this.page = 1; this.render(); }, options);
      this.sort.addEventListener('change', () => { this.page = 1; this.render(); }, options);
      this.reset.addEventListener('click', () => {
        this.type.value = '';
        this.sort.value = this.sort.querySelector('option[value="manual"]') ? 'manual' : this.sort.options[0]?.value || '';
        this.page = 1;
        this.render();
      }, options);
      this.previous.addEventListener('click', () => { this.page -= 1; this.render(true); }, options);
      this.next.addEventListener('click', () => { this.page += 1; this.render(true); }, options);
      this.render();
      this.controls.hidden = this.cards.length === 0;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    /** @param {boolean} moveFocus - Page buttons move focus to the resulting product list. */
    render(moveFocus = false) {
      const matched = this.cards.filter(card => !this.type.value || card.dataset.productType === this.type.value);
      const mode = this.sort.value;
      matched.sort((a, b) => {
        let result = 0;
        if (mode === 'price-ascending' || mode === 'price-descending') {
          result = Number(a.dataset.productPrice) - Number(b.dataset.productPrice);
        } else if (mode === 'title-ascending' || mode === 'title-descending') {
          result = a.dataset.productTitle.localeCompare(b.dataset.productTitle);
        }
        if (mode.endsWith('-descending')) result *= -1;
        return result || this.originalOrder.get(a) - this.originalOrder.get(b);
      });
      const pages = Math.max(1, Math.ceil(matched.length / this.pageSize));
      this.page = Math.min(pages, Math.max(1, this.page));
      const start = (this.page - 1) * this.pageSize;
      const shown = new Set(matched.slice(start, start + this.pageSize));
      for (const card of this.cards) card.hidden = !shown.has(card);
      // Moving existing nodes retains native links and does not introduce HTML from data attributes.
      for (const card of matched) this.grid.append(card);
      const template = matched.length === 1 ? this.dataset.countOne : this.dataset.countOther;
      this.count.textContent = (template || '__count__').replaceAll('__count__', String(matched.length));
      this.empty.hidden = matched.length !== 0;
      this.pagination.hidden = pages <= 1;
      this.previous.disabled = this.page <= 1;
      this.next.disabled = this.page >= pages;
      this.pageLabel.textContent = (this.dataset.pageLabel || '__number__').replaceAll('__number__', `${this.page} / ${pages}`);
      if (moveFocus) {
        this.grid.focus({ preventScroll: true });
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.grid.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    }
  }

  customElements.define('skrt-flash-catalog', SkrtFlashCatalog);
})();
